import { useState, useEffect } from 'react';
import { authFetch } from '@/utils/auth-fetch';

export interface ImpactAvailability {
  available: boolean | null;  // null = loading
  error: string | null;
}

/**
 * Checks whether the 3A impact product is configured for the given site.
 * Calls GET /api/impact-products/:siteId/availability.
 * Returns the real error text on non-OK responses — never silently hides failures.
 * The caller is responsible for any tab-reset that depends on the result.
 *
 * A result is bound to the site it was fetched for. On the first render after a site switch,
 * the previous site's answer is reported as pending (available=null, error=null), never as the
 * new site's — a stale `false` let the AEO Impact page start its heavy legacy request for a site
 * that then opened on the 3A tabs (codex review, PR #7809).
 */
export function useImpactAvailability(siteId: string | undefined): ImpactAvailability {
  const [result, setResult] = useState<{ siteId: string; available: boolean | null; error: string | null } | null>(null);

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    setResult(null);
    authFetch(`/api/impact-products/${siteId}/availability`)
      .then(async (r) => {
        if (cancelled) return;
        if (!r.ok) {
          const body = await r.json().catch(() => ({})) as { error?: string };
          if (cancelled) return;
          setResult({ siteId, available: null, error: body.error ?? `Availability check failed (${r.status})` });
          return;
        }
        const body = await r.json();
        if (cancelled) return;
        setResult({ siteId, available: body.available === true, error: null });
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setResult({ siteId, available: null, error: err.message ?? 'Network error checking availability' });
      });
    return () => { cancelled = true; };
  }, [siteId]);

  if (!result || result.siteId !== siteId) return { available: null, error: null };
  return { available: result.available, error: result.error };
}
